/**
 * Keeping a relation label off the cards, against LIVE positions.
 *
 * The layout used to place every label as part of its one pass, which worked
 * until a card moved: the label stayed where the card had been. Now the edge
 * places its own label, and it does so from the path it just drew and the
 * rectangles the cards currently occupy — so the answer is right on the first
 * frame and still right while a card is being dragged.
 *
 * The rule is the old one, kept because it was the right rule: walk out from
 * the middle of the edge, take the first spot whose pill touches no card, and
 * if there is no such spot show nothing rather than print a word across a
 * card's name. The relation stays on the path's `<title>` either way.
 */

/** An axis-aligned rectangle in flow coordinates. */
export interface Rect {
	left: number;
	right: number;
	top: number;
	bottom: number;
}

/** Fractions along the edge to try, working outward from the middle. */
export const LABEL_T_CANDIDATES = [0.5, 0.4, 0.6, 0.3, 0.7, 0.22, 0.78] as const;

/** Advance width of the 10.5px/600 uppercase pill text, plus its padding. */
const LABEL_CHAR_WIDTH = 7;
const LABEL_PADDING = 12;
const LABEL_HEIGHT = 18;
/** Cards are treated as slightly larger than they are, so a pill cannot graze one. */
const CARD_MARGIN = 3;

export function labelPillRect(x: number, y: number, text: string): Rect {
	const width = text.length * LABEL_CHAR_WIDTH + LABEL_PADDING;
	return {
		left: x - width / 2,
		right: x + width / 2,
		top: y - LABEL_HEIGHT / 2,
		bottom: y + LABEL_HEIGHT / 2,
	};
}

export function overlaps(a: Rect, b: Rect): boolean {
	return (
		a.left < b.right + CARD_MARGIN &&
		b.left - CARD_MARGIN < a.right &&
		a.top < b.bottom + CARD_MARGIN &&
		b.top - CARD_MARGIN < a.bottom
	);
}

/**
 * Sample the drawn path itself rather than re-deriving the bézier's control
 * points, which would be a copy of xyflow's internals that could drift from the
 * line actually on screen.
 *
 * `SVGPathElement` geometry is unimplemented in jsdom, so this returns `null`
 * there and the caller falls back to the midpoint xyflow hands it — the tests
 * that care about labels assert which edges carry one, not where it sits.
 */
export function samplePath(
	d: string,
	fractions: readonly number[],
): { x: number; y: number }[] | null {
	if (typeof document === "undefined") return null;
	try {
		const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
		path.setAttribute("d", d);
		const total = path.getTotalLength();
		if (!Number.isFinite(total) || total === 0) return null;
		return fractions.map((t) => {
			const point = path.getPointAtLength(total * t);
			return { x: point.x, y: point.y };
		});
	} catch {
		return null;
	}
}

/**
 * Where this edge's label should sit, or `null` for "nowhere clear — stay
 * silent".
 *
 * `cards` is every card on the canvas, this edge's own two included: a pill
 * over the card an edge points AT hides that card's name, which is no better
 * than hiding a stranger's.
 */
export function placeEdgeLabel(
	d: string,
	text: string,
	cards: readonly Rect[],
	fallback: { x: number; y: number },
): { x: number; y: number } | null {
	const points = samplePath(d, LABEL_T_CANDIDATES);
	if (points === null) return fallback;

	for (const point of points) {
		const pill = labelPillRect(point.x, point.y, text);
		if (!cards.some((card) => overlaps(pill, card))) return point;
	}
	return null;
}
